/**
 * @file VehicleLoadModal.js
 * @version 1.11.2
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-06-18
 * @modified 2026-10-12
 * @description Drill-down for one LOAD on the transport board: a vehicle summary
 *              box, an efficiency ring, and the load's orders rendered in the lorry
 *              compartments (top table row = first drop = REAR-most compartment).
 *
 *              The board's drop order is the user's plan, so only three layouts are
 *              shown up front: the one applied now, the most efficient IN THAT
 *              ORDER ("Auto" applies this — doors only, rows untouched), and, if it
 *              would beat that, the most efficient layout that needs the rows
 *              re-ordered — flagged, and only ever applied if the user picks it.
 *              The remaining configurations are revealed on click. Hovering any
 *              config animates the doors/compartments to preview it.
 *
 *              1.10.0 (2026-10-07): Loads and Drops — a compartment names the
 *              drop it carries in the lorry's day (its `dropNumber`), and a
 *              load laid out around what is already on board (`configsNote`)
 *              shows the day's layout without offering layouts of its own.
 *
 *              1.11.0 (2026-10-07): `applyBlocked` (a reason) greys the apply
 *              and Auto buttons on a locked or past day, or without
 *              permission, instead of hiding them.
 *
 *              1.11.2 (2026-10-12): the lorry picture is chosen by lorryArt in
 *              LorryDrawing.js, shared with the Lorry day window. Unchanged.
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './VehicleModal.css';
import './VehicleLoadModal.css';
import ProgressRing from './ProgressRing';
import { lorryArt } from './LorryDrawing';
import {
  usablePayload, enumerateConfigs, packLoad, efficiencySeverity, SEVERITY_COLOR,
  bestInSequenceConfig, bestResequencedConfig
} from '../utils/vehicleLoad';
import { formatQuantity } from '../utils/numberFormatters';

const fmtType = (t) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : '—');
// Tonnes by the quantity rule ("12.50", "12.375"; the caller adds the T).
// Named t1 from when it was one decimal place.
const t1 = (n) => (Number.isFinite(Number(n)) ? formatQuantity(Number(n)) : '—');

const lineTonneOf = (row) => {
  const t = Number(row?.line_tonnes);
  return Number.isFinite(t) ? t : (Number(row?.quantity) || 0);
};

// Product key used for the same-product mixing rule (must match analyzeVehicleLoad).
const productOf = (row) => (row?.product_code || row?.product_name || null);

const sameDoors = (a, b) => {
  if (!a && !b) return true;
  if (!a || !b || a.length !== b.length) return false;
  const x = [...a].sort((m, n) => m - n);
  const y = [...b].sort((m, n) => m - n);
  return x.every((v, i) => Math.abs(v - y[i]) < 1e-6);
};
const isIdentityOrder = (order) => Array.isArray(order) && order.every((v, i) => v === i);

// What to render inside one compartment from its assignment entry. Returns null
// for an empty compartment. Handles a split order (its `portion` is this
// compartment's share, not the whole order) and a same-product mixed compartment.
const compContent = (a, rows) => {
  if (a.lineIndex == null) return null;
  const fillPct = a.fill != null
    ? Math.min(a.fill * 100, 100)
    : (a.size > 0 ? Math.min((a.portion / a.size) * 100, 100) : 0);
  if (a.mixed && Array.isArray(a.lines) && a.lines.length > 1) {
    return { drop: `DROPS ${a.lines.map(li => li + 1).join('+')}`, name: 'Mixed · same product', fillPct };
  }
  const row = rows[a.lineIndex];
  // Loads and Drops numbers drops across the lorry's whole day.
  const drop = a.dropNumber != null ? a.dropNumber : a.lineIndex + 1;
  return { drop: `DROP ${drop}`, name: row?.customer_name || '—', fillPct };
};

function VehicleLoadModal({ isOpen, load, vehicle, onClose, onApply, applyBlocked = null, onSetUnknownPayload }) {
  // { idx, mode } — mode 'seq' previews the layout in the user's drop order,
  // 'reseq' previews it in the re-ordered sequence it would need.
  const [hovered, setHovered] = useState(null);
  const [showAll, setShowAll] = useState(false);
  // Draft for the "Unknown" vehicle's payload (no fleet spec — just a payload).
  const [payloadDraft, setPayloadDraft] = useState('');
  useEffect(() => { setHovered(null); setShowAll(false); }, [load]);
  useEffect(() => {
    setPayloadDraft(vehicle?.payload != null ? String(vehicle.payload) : '');
  }, [load, vehicle?.payload]);

  if (!isOpen || !load || !vehicle) return null;

  const isUnknown = !!load.isUnknown;
  const commitPayload = () => {
    if (!onSetUnknownPayload) return;
    const v = payloadDraft.trim();
    onSetUnknownPayload(v === '' ? null : Number(v));
  };

  const art = lorryArt(vehicle);
  const eff = usablePayload(vehicle);
  const moveable = !!vehicle.has_moveable_doors;
  const compartmentCount = load.compartmentCount || 0;
  const sevColor = SEVERITY_COLOR[efficiencySeverity(load)];

  const lineTonnes = load.rows.map(lineTonneOf);
  const products = load.rows.map(productOf);
  // A Loads and Drops load laid out around what is on board has no layouts of
  // its own to offer — the day decides them.
  const configs = load.configsNote ? [] : enumerateConfigs(vehicle, lineTonnes, products);
  const currentIdx = configs.findIndex(c => sameDoors(c.doors, load.doorConfig));

  // The drop order in the table is the user's plan. "Auto" only moves the doors —
  // it picks the most efficient layout that works IN THAT ORDER, never re-ordering.
  const bestSeq = bestInSequenceConfig(configs);
  // A layout that would beat it but needs the rows re-ordered — flagged for the
  // user to accept or ignore; never applied automatically.
  const bestReseq = bestResequencedConfig(configs, bestSeq);
  const bestSeqIdx = bestSeq ? configs.indexOf(bestSeq) : -1;
  const bestReseqIdx = bestReseq ? configs.indexOf(bestReseq) : -1;

  const handleAuto = () => { if (bestSeq) onApply(load, null, bestSeq.doors); };

  const identitySeq = lineTonnes.map((_, i) => i);
  const orderFor = (c, mode) => (mode === 'seq' ? identitySeq : (c.dropOrder || identitySeq));

  // Which config the lorry previews: hovered → current → best-in-order → best re-ordered.
  const defaultPreview = currentIdx >= 0
    ? { idx: currentIdx, mode: configs[currentIdx].fitsCurrentSequence ? 'seq' : 'reseq' }
    : (bestSeqIdx >= 0 ? { idx: bestSeqIdx, mode: 'seq' }
      : (bestReseqIdx >= 0 ? { idx: bestReseqIdx, mode: 'reseq' } : null));
  const preview = hovered || defaultPreview;
  const previewCfg = preview ? configs[preview.idx] : null;

  // Block-pack the previewed config (an order may span several contiguous
  // compartments). Falls back to an all-empty layout when the config can't carry
  // the load, or to the analysed assignment when no config is previewed.
  const emptyLayout = (cfg) => cfg.sizes.map(size => ({ size, lineIndex: null, lines: null, portion: 0, fill: null, mixed: false }));
  const previewAssignment = previewCfg
    ? (packLoad(previewCfg.sizes, orderFor(previewCfg, preview.mode), lineTonnes, products) || emptyLayout(previewCfg))
    : (load.assignment || null);

  // Available door slot positions (cumulative t from front) for static markers.
  const slotPositions = (eff != null && eff > 0)
    ? Array.from(new Set(
        (Array.isArray(vehicle.door_positions) ? vehicle.door_positions : [])
          .map(d => Number(d?.tonnes))
          .filter(t => Number.isFinite(t) && t > 0 && t < eff)
      )).sort((a, b) => a - b)
    : [];

  const compartmentBlock = (() => {
    if (!previewAssignment || !previewAssignment.length) {
      return (
        <div className="vm-compartments-empty vlm-nofit">
          <div className="vlm-nofit-msg">
            {load.fit === 'too-many'
              ? `${load.lineCount} drops but only ${compartmentCount} compartment(s) — can't lay out.`
              : load.fit === 'wont-fit'
                ? (load.messages[0] || "These orders won't fit this vehicle's compartments:")
                : (load.messages[0] || 'No compartment layout for this vehicle.')}
          </div>
          <ul className="vlm-nofit-list">
            {load.rows.map((r, i) => (
              <li key={i}>
                <strong>{t1(lineTonneOf(r))} T</strong> — {r.customer_name || `Drop ${i + 1}`}
                {r.product_name ? <span className="vlm-nofit-prod"> · {r.product_name}</span> : ''}
              </li>
            ))}
          </ul>
        </div>
      );
    }

    if (moveable) {
      if (eff == null || eff <= 0) return <div className="vm-compartments-empty">Set a payload to lay out compartments.</div>;
      const f2b = previewAssignment.slice().reverse(); // rear→front → front→back for display
      let cum = 0;
      const segs = f2b.map((a, j) => {
        const left = cum; cum += a.size;
        return { ...a, frontIdx: j, leftPct: (left / eff) * 100, widthPct: (a.size / eff) * 100 };
      });
      const previewDoors = previewCfg?.doors ? [...previewCfg.doors].sort((x, y) => x - y) : [];
      return (
        <div className="vm-doors-overlay vlm-overlay">
          {/* available slots (static, faint) */}
          {slotPositions.map((pos) => (
            <div key={`slot-${pos}`} className="vlm-slot" style={{ left: `${(pos / eff) * 100}%` }} />
          ))}
          {/* compartments (animate when the preview changes) */}
          {segs.map((s) => {
            const info = compContent(s, load.rows);
            return (
              <div
                key={`seg-${s.frontIdx}`}
                className={`vm-door-seg vlm-seg ${info ? '' : 'vlm-empty'} ${s.mixed ? 'vlm-mixed' : ''}`}
                style={{ left: `${s.leftPct}%`, width: `${s.widthPct}%` }}
                title={info ? `${t1(s.portion)}T in ${t1(s.size)}T — ${Math.round(info.fillPct)}% full` : `empty ${t1(s.size)}T`}
              >
                <div className="vlm-fill" style={{ height: `${info ? info.fillPct : 0}%` }} />
                {info ? (
                  <>
                    <span className="vm-door-seg-index">{info.drop}</span>
                    <span className="vl-seg-order" title={info.name}>{info.name}</span>
                    <span className="vm-door-seg-value">
                      <span className="vm-door-seg-num">{t1(s.portion)}</span>
                      <span className="vm-comp-unit">T</span>
                      <span className="vlm-of">/{t1(s.size)}T</span>
                    </span>
                  </>
                ) : (
                  <span className="vlm-empty-cap"><em>empty</em><br />{t1(s.size)}<span> T</span></span>
                )}
              </div>
            );
          })}
          {/* active doors (animate sliding between configs) */}
          {previewDoors.map((pos, di) => (
            <div key={`door-${di}`} className="vlm-door" style={{ left: `${(pos / eff) * 100}%` }}>
              {/* A door position is a vehicle setting, not a quantity: left as it was. */}
              <span className="vlm-door-label">{Number(pos).toFixed(1)}T</span>
            </div>
          ))}
        </div>
      );
    }

    // Fixed compartments: equal-width cells front→back; rear-most ones filled.
    const f2b = previewAssignment.slice().reverse(); // rear→front → front→back for display
    return (
      <div className="vm-compartments">
        {f2b.map((a, idx) => {
          const info = compContent(a, load.rows);
          return (
            <div
              key={idx}
              className={`vm-comp vl-comp ${info ? 'vm-comp-filled' : 'vl-comp-empty'} ${a.mixed ? 'vlm-mixed' : ''}`}
              title={info ? `${t1(a.portion)}T in ${t1(a.size)}T — ${Math.round(info.fillPct)}% full` : `empty ${t1(a.size)}T`}
            >
              <div className="vlm-fill" style={{ height: `${info ? info.fillPct : 0}%` }} />
              {info ? (
                <>
                  <span className="vm-comp-index">{info.drop}</span>
                  <span className="vl-seg-order" title={info.name}>{info.name}</span>
                  <span className="vm-comp-value">
                    <span className="vm-door-seg-num">{t1(a.portion)}</span>
                    <span className="vm-comp-unit">T</span>
                    <span className="vlm-of">/{t1(a.size)}T</span>
                  </span>
                </>
              ) : (
                <span className="vl-empty-label">empty<br />{Number.isFinite(a.size) ? `${t1(a.size)}T` : ''}</span>
              )}
            </div>
          );
        })}
      </div>
    );
  })();

  // Only three layouts matter up front: the one applied now, the most efficient
  // in the user's own drop order, and (if it beats that) the most efficient one
  // that would need the rows re-ordered. Everything else hides behind "show all".
  const withIdx = configs.map((c, idx) => ({ c, idx }));
  const featured = [];
  const addFeatured = (idx, mode, label) => {
    if (idx < 0 || !configs[idx]) return;
    const hit = featured.find(f => f.idx === idx && f.mode === mode);
    if (hit) { if (label && !hit.labels.includes(label)) hit.labels.push(label); return; }
    featured.push({ c: configs[idx], idx, mode, labels: label ? [label] : [] });
  };
  if (currentIdx >= 0) {
    addFeatured(currentIdx, configs[currentIdx].fitsCurrentSequence ? 'seq' : 'reseq', 'Current');
  }
  addFeatured(bestSeqIdx, 'seq', 'Most efficient in your order');
  addFeatured(bestReseqIdx, 'reseq', 'Most efficient — needs re-order');
  const others = withIdx
    .filter(x => !featured.some(f => f.idx === x.idx))
    .map(x => ({ ...x, mode: x.c.fitsCurrentSequence ? 'seq' : 'reseq', labels: [] }));

  const renderConfig = ({ c, idx, mode, labels }) => {
    const key = `${idx}:${mode}`;
    const isCurrent = idx === currentIdx && labels.includes('Current');
    const needsReorder = mode === 'reseq' && !isIdentityOrder(c.dropOrder);
    // In-sequence entries only move the doors; a re-order entry also re-sequences
    // the table — and only ever when the user explicitly picks it.
    const usable = mode === 'seq' ? c.fitsCurrentSequence : c.fits;
    const shownEff = mode === 'seq' ? c.seqEfficiency : c.efficiency;
    const shownMixed = mode === 'seq' ? c.seqMixed : c.mixed;
    // A locked or past day (or no permission) greys the button with the reason — never hides it.
    const canApply = usable && (onApply || applyBlocked) && !(isCurrent && !needsReorder);
    // Apply but keep the modal open — the parent re-analyses the load and passes
    // the fresh version straight back in, so it refreshes in place. Rendered as a
    // <span role="button"> (not a <button>) so the native button box can't squash
    // the icon vertically.
    const apply = (e) => {
      e.stopPropagation();
      onApply(load, needsReorder ? c.dropOrder : null, c.doors);
    };
    const applyKey = (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); apply(e); } };
    // Per-compartment fill for this config (front → rear for display, cab on the
    // left). Each compartment is a square whose primary fill shows how full it is.
    const packed = usable ? packLoad(c.sizes, orderFor(c, mode), lineTonnes, products) : null;
    const comps = (packed || c.sizes.map(size => ({ size, portion: 0, fill: null, lineIndex: null }))).slice().reverse();
    const isHovered = hovered && hovered.idx === idx && hovered.mode === mode;
    return (
      <div
        key={key}
        className={`vl-config ${mode === 'seq' && c.fitsCurrentSequence ? 'vl-config-fits' : ''} ${usable ? '' : 'vl-config-nofit'} ${isHovered ? 'vl-config-hover' : ''} ${isCurrent ? 'vl-config-current' : ''} ${needsReorder ? 'vl-config-reseq' : ''}`}
        onMouseEnter={() => setHovered({ idx, mode })}
        onMouseLeave={() => setHovered(null)}
      >
        {labels.length > 0 && (
          <div className="vl-config-labels">
            {labels.map(l => (
              <span key={l} className={`vl-config-label ${l === 'Current' ? 'vl-label-current' : ''} ${l.includes('re-order') ? 'vl-label-reseq' : ''}`}>{l}</span>
            ))}
          </div>
        )}
        <div className="vl-config-comps">
          {comps.map((a, k) => {
            const empty = a.lineIndex == null || a.portion <= 0;
            const fillPct = a.fill != null ? Math.min(a.fill * 100, 100)
              : (!empty && a.size > 0 ? Math.min((a.portion / a.size) * 100, 100) : 0);
            return (
              <span
                key={k}
                className={`vl-cfg-comp ${empty ? 'vl-cfg-comp-empty' : ''}`}
                title={empty ? `empty ${t1(a.size)}T` : `${t1(a.portion)}T in ${t1(a.size)}T — ${Math.round(fillPct)}% full`}
              >
                {!empty && <span className="vl-cfg-comp-fill" style={{ height: `${fillPct}%` }} />}
                <span className="vl-cfg-comp-cap">{t1(a.size)}</span>
              </span>
            );
          })}
        </div>
        <div className="vl-config-foot">
          {shownEff != null && (
            <span className="vl-config-eff" style={{ color: SEVERITY_COLOR[efficiencySeverity({ efficiency: shownEff })] }}>
              {shownEff}%
            </span>
          )}
          {isCurrent && (
            <span className="vl-config-badge" title={load.isOverride ? 'Manual layout — currently applied' : 'Currently applied layout'}>
              <span className="material-symbols-rounded">check_circle</span>
            </span>
          )}
          {usable && shownMixed && <span className="vl-config-mixed-badge" title="Two same-product orders share a compartment">mix</span>}
          {!usable && <span className="vl-config-nofit-badge">won't fit</span>}
          {canApply && (
            <span
              className={`vl-cfg-btn${onApply ? '' : ' disabled'}`}
              role="button"
              tabIndex={onApply ? 0 : -1}
              aria-disabled={!onApply}
              onClick={onApply ? apply : (e) => e.stopPropagation()}
              onKeyDown={onApply ? applyKey : undefined}
              title={!onApply ? applyBlocked : (needsReorder
                ? 'Re-order this load’s drops to this sequence and use this layout'
                : 'Use this door layout — your drop order is kept')}
            >
              <span className="material-symbols-rounded">{needsReorder ? 'swap_vert' : 'check_circle'}</span>
            </span>
          )}
        </div>
      </div>
    );
  };

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content vehicle-load-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div style={{ display: 'flex', marginBottom: 'var(--spacing-md)' }}>
            <h2>
              {vehicle.registration}
              {load.driver && <span className="vl-driver-tag"><span className="material-symbols-rounded">person</span>{load.driver}</span>}
              <span className="vl-load-tag">Load {(load.loadIndex ?? 0) + 1}{load.loadCount ? ` of ${load.loadCount}` : ''}</span>
            </h2>
            <button className="modal-close" onClick={onClose}>
              <span className="material-symbols-rounded">close</span>
            </button>
          </div>
          <div className="vl-collect">
            <span className="material-symbols-rounded">inventory_2</span>
            Collect from <strong>{load.supplier || '—'}</strong>
            {load.time && <> at <strong>{String(load.time).slice(0, 5)}</strong></>}
            {' · '}{load.lineCount} drop{load.lineCount === 1 ? '' : 's'}
          </div>
        </div>

        <div className="modal-body">
          <div className="vl-sticky-top">
          {isUnknown ? (
            <div className="vl-unknown-payload">
              <span className="material-symbols-rounded">scale</span>
              <div className="vl-unknown-fields">
                <label htmlFor="vl-unknown-payload-input">Vehicle payload (T)</label>
                <div className="vl-unknown-input-row">
                  <input
                    id="vl-unknown-payload-input"
                    type="number"
                    min="0"
                    step="0.1"
                    className="form-input"
                    placeholder="e.g. 29"
                    value={payloadDraft}
                    onChange={(e) => setPayloadDraft(e.target.value)}
                    onBlur={commitPayload}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitPayload(); } }}
                    disabled={!onSetUnknownPayload}
                    autoFocus
                  />
                </div>
                <span className="vl-unknown-hint">
                  Applies to {load.driver ? <strong>{load.driver}</strong> : 'this driver'} across the day’s plan.
                </span>
              </div>
            </div>
          ) : (
            <div className={`vm-lorry ${art.cls}`}>
              <div className="vm-lorry-frame">
                <img className="vm-lorry-img" src={art.src} alt="Lorry" />
                {compartmentBlock}
              </div>
            </div>
          )}

          {load.mixed && (
            <div className="vl-mixed-banner">
              <span className="material-symbols-rounded">merge</span>
              Two same-product orders are sharing a compartment for this load.
            </div>
          )}

          <div className="vl-grid">
            <div className="vl-summary">
              <div className="vl-summary-title">Vehicle</div>
              <dl>
                {isUnknown ? (
                  <>
                    <dt>Vehicle</dt><dd>Unknown (payload only)</dd>
                    <dt>Driver</dt><dd>{load.driver || '—'}</dd>
                    <dt>Payload</dt><dd>{vehicle.payload != null ? `${t1(vehicle.payload)} T` : 'not set'}</dd>
                  </>
                ) : (
                  <>
                    <dt>Type</dt><dd>{fmtType(vehicle.vehicle_type)}{vehicle.has_moffett ? ' + Moffett' : ''}</dd>
                    <dt>Configuration</dt><dd>{vehicle.axle_configuration || '—'}</dd>
                    <dt>Payload</dt><dd>{vehicle.payload != null ? `${t1(vehicle.payload)} T` : '—'}{vehicle.has_moffett && eff != null ? ` (${t1(eff)} T usable)` : ''}</dd>
                    <dt>Gross weight</dt><dd>{vehicle.gross_weight != null ? `${t1(vehicle.gross_weight)} T` : '—'}</dd>
                    <dt>Body</dt><dd>{moveable ? `${compartmentCount} comps (moveable)` : `${compartmentCount} comps`}</dd>
                    <dt>Rear lift</dt><dd>{vehicle.rear_lift ? 'Yes' : 'No'}</dd>
                    <dt>Default driver</dt><dd>{vehicle.default_driver_name || '—'}</dd>
                    <dt>Default haulier</dt><dd>{vehicle.default_haulier_name || '—'}</dd>
                  </>
                )}
              </dl>
            </div>

            <div className="vl-efficiency">
              <ProgressRing percentage={load.efficiency ?? 0} label={isUnknown ? 'Payload' : 'Efficiency'} color={sevColor} size={70} />
              <div className="vl-eff-breakdown">
                <div><span>Payload used</span><strong>{load.payloadUtil}%</strong></div>
                <div><span>Payload (T)</span><em><strong>{t1(load.totalTonnes)}</strong> /{eff != null ? t1(eff) : '—'} T</em></div>
                {!isUnknown && <div><span>Compartment fill</span><strong>{load.compartmentFill}%</strong></div>}
                {!isUnknown && <div><span>Compartments</span><strong>{load.compartmentsUsed}/{compartmentCount || '—'}</strong></div>}
                {load.overPayload && <div className="vl-eff-warn">Over payload by {t1(load.overBy)} T</div>}
              </div>
            </div>
          </div>

          {load.configsNote && (
            <div className="vl-reseq-notice">
              <span className="material-symbols-rounded">route</span>
              {load.configsNote}
              {Array.isArray(load.doorConfig) && load.doorConfig.length > 0 && ` Doors at ${load.doorConfig.map(d => `${d}T`).join(', ')}.`}
            </div>
          )}

          {configs.length > 0 && (
            <div className="vl-summary-title vl-configs-title">
              {moveable ? `Door configuration` : 'Compartment layout'}
              {moveable && <span className="vl-configs-hint"> — hover to preview</span>}
              {(onApply || applyBlocked) && bestSeq && (
                <button
                  className="vl-revert-auto"
                  disabled={!onApply}
                  title={onApply ? 'Auto: use the most efficient layout that works in your drop order (rows are not re-ordered)' : applyBlocked}
                  onClick={handleAuto}
                >
                  <span className="material-symbols-rounded">auto_fix_high</span>
                  Auto
                </button>
              )}
            </div>
          )}
          </div>

          {configs.length > 0 && (
            <div className="vl-configs vl-configs-scroll">
              {bestReseq && (
                <div className="vl-reseq-notice">
                  <span className="material-symbols-rounded">swap_vert</span>
                  A {bestReseq.efficiency}% layout is possible if the drops are re-ordered
                  {bestSeq?.seqEfficiency != null && ` (vs ${bestSeq.seqEfficiency}% in your order)`}.
                  Your order is kept unless you choose it.
                </div>
              )}
              <div className="vl-config-list">
                {featured.map(renderConfig)}
              </div>
              {others.length > 0 && (
                <div className={`vl-config-extra ${showAll ? 'open' : ''}`}>
                  <div className="vl-config-list vl-config-list-extra">
                    {others.map(renderConfig)}
                  </div>
                </div>
              )}
              <div className="vl-configs-note">
                {others.length > 0 && (
                  <button className="vl-show-all" onClick={() => setShowAll(s => !s)}>
                    {showAll ? 'Hide other configurations' : `Show ${others.length} other configuration${others.length === 1 ? '' : 's'}`}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default VehicleLoadModal;
